import i18n from '@/utils/i18n';
import React from 'react';
import {
  AccountDetailMetric,
  MetricsTimeRange,
} from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-detail-metrics-loader';
import { AccountDetailMetricsList } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-metrics-list';
import { AccountDetailMetricsSkeleton } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-metrics-skeleton';
import { Chip, Typography } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { DetailSectionEmptyState } from '@/experiences/sales-portal/components/detail-section-empty-state';
import { MetricDataBox } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/metric-data-box';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';
import { View } from 'react-native';

const TIME_RANGE_OPTIONS = [
  MetricsTimeRange.WTD,
  MetricsTimeRange.MTD,
  MetricsTimeRange.YTD,
] as const;

interface AccountDetailMetricsProps {
  metrics: AccountDetailMetric[] | null;
  onTimeRangeChange: (timeRange: MetricsTimeRange) => void;
  selectedTimeRange: MetricsTimeRange;
}

export const AccountDetailMetrics: React.FC<AccountDetailMetricsProps> = ({
  metrics,
  onTimeRangeChange,
  selectedTimeRange,
}) => {
  const [styles] = useStyles();
  const isMobileBottomNavLayout = useIsMobileBottomNavLayout();

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.sectionHeader,
          isMobileBottomNavLayout ? null : styles.sectionHeaderDesktop,
        ]}
      >
        <Typography
          accessibilityRole="header"
          bold={!isMobileBottomNavLayout}
          size={isMobileBottomNavLayout ? '$lg' : '$md'}
          variant="heading"
        >
          {i18n.t('sales_portal.account_detail.metrics.key_metrics')}
        </Typography>
        <View style={styles.metricsDateRangeRow}>
          {TIME_RANGE_OPTIONS.map((range) => (
            <Chip
              circular
              key={range}
              onPress={() => onTimeRangeChange(range)}
              pressable
              selected={selectedTimeRange === range}
              size={!isMobileBottomNavLayout ? '$sm' : '$md'}
            >
              <Chip.Text>{range}</Chip.Text>
            </Chip>
          ))}
        </View>
      </View>
      {metrics === null ? (
        <AccountDetailMetricsSkeleton />
      ) : metrics.length === 0 ? (
        <DetailSectionEmptyState
          text={i18n.t('sales_portal.account_detail.metrics.empty_state')}
        />
      ) : !isMobileBottomNavLayout ? (
        <View style={styles.dataBoxRow}>
          {metrics.map((metric) => (
            <MetricDataBox
              changePercentage={metric.changePercentage}
              key={metric.label}
              label={metric.label}
              value={metric.value}
            />
          ))}
        </View>
      ) : (
        <AccountDetailMetricsList metrics={metrics} />
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    gap: theme.sk.spacing.spaceSmall,
  },
  dataBoxRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  metricsDateRangeRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: theme.sk.spacing.spaceXsmall,
  },
  sectionHeaderDesktop: {
    paddingTop: theme.sk.spacing.spaceMedium,
  },
}));
